import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Ban, Link2, Mail, ReceiptText, RotateCcw, Repeat } from 'lucide-react';
import { Button } from '@/components/button';
import { Card, CardBody } from '@/components/card';
import { DataList, DataListRow } from '@/components/data-list';
import { Dialog } from '@/components/dialog';
import { EmptyState } from '@/components/empty-state';
import { Menu } from '@/components/menu';
import { Select } from '@/components/select';
import { EmailComposerDialog, type ComposedEmail } from '@/components/email-composer';
import { SkeletonList } from '@/components/skeleton';
import { StatusPill } from '@/components/status-dot';
import { useToast } from '@/components/toast';
import { useClients } from '@/lib/clients';
import { formatDateLong, formatMoney } from '@/lib/format';
import { summarizeRecipients } from '@/lib/mail-recipients';
import {
  useDismissSubscriptionCharge,
  useLinkSubscriptionChargeClient,
  useReceiptPdf,
  useReopenSubscriptionCharge,
  useSendSubscriptionChargeReceipt,
  useSubscriptionCharges,
  useSubscriptionChargesSummary,
  type ChargeStatusFilter,
} from '@/lib/subscription-charges';
import type { SubscriptionChargeData } from '@/types/api';

function statusTone(status: string): 'success' | 'warning' | 'danger' | 'neutral' | 'info' {
  if (status === 'sent') return 'success';
  if (status === 'pending_review') return 'warning';
  return 'neutral';
}

function billingPeriod(charge: SubscriptionChargeData): string | null {
  if (!charge.period_start || !charge.period_end) return null;
  return `${formatDateLong(charge.period_start)} to ${formatDateLong(charge.period_end)}`;
}

function SummaryTiles() {
  const { data } = useSubscriptionChargesSummary();
  const unlinked = data?.unlinked_count ?? 0;

  return (
    <div className="grid grid-cols-2 gap-3 sm:max-w-md">
      <Card>
        <CardBody className="py-3">
          <div className="text-xs font-medium uppercase tracking-wide text-[var(--color-foreground-subtle)]">
            Awaiting review
          </div>
          <div className="mt-1 text-lg font-semibold tabular-nums">
            {formatMoney(data?.pending_cents ?? 0, 'USD')}
          </div>
          <div className="text-xs text-muted-foreground">
            {data?.pending_count ?? 0} {data?.pending_count === 1 ? 'receipt' : 'receipts'}
          </div>
        </CardBody>
      </Card>
      <Card>
        <CardBody className="py-3">
          <div className="text-xs font-medium uppercase tracking-wide text-[var(--color-foreground-subtle)]">
            Unmatched
          </div>
          <div
            className={`mt-1 text-lg font-semibold tabular-nums ${unlinked > 0 ? 'text-[var(--color-warning)]' : ''}`}
          >
            {unlinked}
          </div>
          <div className="text-xs text-muted-foreground">
            {unlinked > 0 ? 'need a client before sending' : 'all matched to a client'}
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

/**
 * Reading the actual receipt is the review. A summary row tells you a charge
 * happened; only the PDF tells you what the client is about to receive.
 */
function ReceiptPreview({ charge, onClose }: { charge: SubscriptionChargeData; onClose: () => void }) {
  const { objectUrl, failed } = useReceiptPdf(charge.pdf_url);

  return (
    <Dialog open onClose={onClose} title={`Receipt for ${charge.plan_label}`} size="lg">
      <div className="space-y-3">
        <div className="text-sm text-muted-foreground">
          {charge.client_name ?? 'Unmatched client'} ·{' '}
          {formatMoney(charge.total_cents, charge.currency)}
          {charge.paid_at ? ` · charged ${formatDateLong(charge.paid_at)}` : ''}
        </div>
        {failed ? (
          <div className="rounded-md border border-border bg-muted p-6 text-center text-sm text-muted-foreground">
            The receipt couldn&rsquo;t be loaded. Try again, or check the logs if it keeps failing.
          </div>
        ) : !objectUrl ? (
          <div className="flex h-[60vh] w-full items-center justify-center rounded-md border border-border bg-muted text-sm text-muted-foreground">
            Rendering the receipt…
          </div>
        ) : (
          <object
            data={objectUrl}
            type="application/pdf"
            className="h-[60vh] w-full rounded-md border border-border bg-muted"
            aria-label={`Receipt PDF for ${charge.plan_label}`}
          >
            <div className="p-6 text-center text-sm text-muted-foreground">
              <p>Your browser can&rsquo;t display the PDF inline.</p>
              <a className="text-foreground underline" href={objectUrl} target="_blank" rel="noreferrer">
                Open it in a new tab
              </a>
            </div>
          </object>
        )}
      </div>
    </Dialog>
  );
}

function LinkClientDialog({
  charge,
  onClose,
}: {
  charge: SubscriptionChargeData;
  onClose: () => void;
}) {
  const clients = useClients({ status: 'active' });
  const [clientId, setClientId] = useState('');
  const link = useLinkSubscriptionChargeClient();
  const toast = useToast();

  async function onSubmit() {
    if (!clientId) return;
    try {
      await link.mutateAsync({ id: charge.id, body: { client_id: Number(clientId) } });
      toast.success('Charge linked to client');
      onClose();
    } catch (err) {
      toast.error('Could not link it', err instanceof Error ? err.message : undefined);
    }
  }

  return (
    <Dialog open onClose={onClose} title="Link this charge to a client">
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Stripe customer <code className="text-xs">{charge.stripe_invoice_id}</code> matched no
          client. Linking also claims any other unmatched charges from the same customer, and
          teaches future renewals who they belong to.
        </p>
        <Select value={clientId} onChange={(e) => setClientId(e.target.value)}>
          <option value="">Choose a client…</option>
          {(clients.data?.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={!clientId || link.isPending}>
            Link client
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

/**
 * The renewal receipts waiting to be reviewed and sent. Lives as the Receipts
 * tab on the Subscriptions page.
 */
export function ReceiptsQueue() {
  const [status, setStatus] = useState<ChargeStatusFilter>('pending_review');
  const [previewing, setPreviewing] = useState<SubscriptionChargeData | null>(null);
  const [sending, setSending] = useState<SubscriptionChargeData | null>(null);
  const [linking, setLinking] = useState<SubscriptionChargeData | null>(null);

  const { data: charges, isLoading } = useSubscriptionCharges(status);
  const sendReceipt = useSendSubscriptionChargeReceipt();
  const dismiss = useDismissSubscriptionCharge();
  const reopen = useReopenSubscriptionCharge();
  const toast = useToast();

  const emptyCopy = useMemo(() => {
    if (status === 'pending_review') {
      return {
        title: 'Nothing waiting',
        description:
          'Stripe subscription renewals land here for review. Send or dismiss them and this stays empty.',
      };
    }
    if (status === 'sent') {
      return { title: 'No receipts sent yet', description: 'Sent receipts are listed here.' };
    }
    return { title: 'Nothing here', description: 'Dismissed charges are listed here.' };
  }, [status]);

  async function onSend(email: ComposedEmail) {
    if (!sending) return;
    // A failure throws back into the composer, which stays open and says why.
    await sendReceipt.mutateAsync({ id: sending.id, ...email });
    toast.success(`Receipt sent for ${sending.plan_label}`, summarizeRecipients(email));
    setSending(null);
  }

  async function run(fn: () => Promise<unknown>, message: string) {
    try {
      await fn();
      toast.success(message);
    } catch (err) {
      toast.error('Something went wrong', err instanceof Error ? err.message : undefined);
    }
  }

  function menuFor(charge: SubscriptionChargeData) {
    const items = [
      {
        key: 'preview',
        label: 'Review receipt',
        icon: ReceiptText,
        onSelect: () => setPreviewing(charge),
      },
    ];

    if (charge.needs_client) {
      items.push({
        key: 'link',
        label: 'Link to a client',
        icon: Link2,
        onSelect: () => setLinking(charge),
      });
    } else if (charge.can_send) {
      items.push({
        key: 'send',
        label: charge.status === 'sent' ? 'Send again' : 'Send receipt',
        icon: Mail,
        onSelect: () => setSending(charge),
      });
    }

    if (charge.status === 'dismissed') {
      items.push({
        key: 'reopen',
        label: 'Move back to review',
        icon: RotateCcw,
        onSelect: () => run(() => reopen.mutateAsync({ id: charge.id }), 'Moved back to review'),
      });
    } else if (charge.status === 'pending_review') {
      items.push({
        key: 'dismiss',
        label: 'Skip, no receipt',
        icon: Ban,
        onSelect: () => run(() => dismiss.mutateAsync({ id: charge.id }), 'Cleared from the queue'),
      });
    }

    return items;
  }

  return (
    <div className="space-y-5">
      <p className="text-sm text-[var(--color-foreground-muted)]">
        Stripe charges the card, you send the receipt. Nothing reaches the client until you do.
      </p>

      <SummaryTiles />

      <div className="flex flex-wrap items-center gap-3">
        <Select
          value={status}
          onChange={(e) => setStatus(e.target.value as ChargeStatusFilter)}
          className="max-w-[200px]"
        >
          <option value="pending_review">Awaiting review</option>
          <option value="sent">Sent</option>
          <option value="dismissed">Dismissed</option>
          <option value="all">All</option>
        </Select>
      </div>

      {isLoading ? (
        <SkeletonList rows={4} />
      ) : !charges || charges.length === 0 ? (
        <EmptyState icon={Repeat} title={emptyCopy.title} description={emptyCopy.description} />
      ) : (
        <DataList>
          {charges.map((charge) => (
            <DataListRow key={charge.id} onClick={() => setPreviewing(charge)} interactive>
              <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-indigo-500/12 text-indigo-700 dark:text-indigo-300">
                <Repeat className="size-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2">
                  <span className="truncate font-medium text-foreground">{charge.plan_label}</span>
                </div>
                <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                  {charge.client_id ? (
                    <Link
                      to={`/clients/${charge.client_id}`}
                      className="hover:text-foreground hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {charge.client_name}
                    </Link>
                  ) : (
                    <span className="text-[var(--color-warning)]">No client matched</span>
                  )}
                  {billingPeriod(charge) ? (
                    <>
                      <span aria-hidden>·</span>
                      <span>{billingPeriod(charge)}</span>
                    </>
                  ) : null}
                </div>
              </div>
              <div className="hidden text-right md:block">
                <div className="text-sm font-medium tabular-nums text-foreground">
                  {formatMoney(charge.total_cents, charge.currency)}
                </div>
                {charge.paid_at ? (
                  <div className="text-xs tabular-nums text-muted-foreground">
                    {formatDateLong(charge.paid_at)}
                  </div>
                ) : null}
              </div>
              {charge.needs_client ? (
                <StatusPill tone="warning">Needs a client</StatusPill>
              ) : (
                <StatusPill tone={statusTone(charge.status)}>{charge.status_label}</StatusPill>
              )}
              <div onClick={(e) => e.stopPropagation()}>
                <Menu items={menuFor(charge)} />
              </div>
            </DataListRow>
          ))}
        </DataList>
      )}

      {previewing ? (
        <ReceiptPreview charge={previewing} onClose={() => setPreviewing(null)} />
      ) : null}

      {linking ? <LinkClientDialog charge={linking} onClose={() => setLinking(null)} /> : null}

      {sending ? (
        <EmailComposerDialog
          source={{ kind: 'record', type: 'subscription.receipt', id: sending.id }}
          onClose={() => setSending(null)}
          title={`Send receipt for ${sending.plan_label}`}
          summary={
            <p className="text-sm text-[var(--color-foreground-muted)]">
              {formatMoney(sending.total_cents, sending.currency)} charged
              {sending.paid_at ? ` on ${formatDateLong(sending.paid_at)}` : ''}. The receipt PDF is attached.
            </p>
          }
          lockedNote="The amount paid, the period it covers, the transaction ID and the signature are added below your message."
          confirmLabel="Send receipt"
          pending={sendReceipt.isPending}
          onSend={onSend}
        />
      ) : null}
    </div>
  );
}
